import { sum } from 'lib/array/arrayUtils'
import { isMultiCartBedbankHotelItem, isMultiCartHotelItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import {
  getCartPanelExperienceCartTime,
  getMultiCartExperienceCheckoutItem,
  toCartPanelExperienceTickets,
  type CartPanelExperienceTicket,
  type CartPanelItemCartStatus,
} from './cartPanelUtils'
import type {
  TripChatAccommodationCartCandidate,
  TripChatExperienceCartCandidate,
} from './tripChatCartCandidateUtils'
import type { CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'

export interface CartPanelAccommodationPricingInput {
  tripItemId: number
  cartStatus: CartPanelItemCartStatus
  offerId: string
  packageId?: string
  checkIn?: string
  checkOut?: string
  occupancy?: App.Occupants
  duration?: number
  roomRateId?: string
  isBedbankHotel: boolean
  savedPrice?: number
  savedMemberPrice?: number
  savedValue?: number
  /** Charter bundle: selection + full occupancies for the live flight price. */
  charterFlightBundle?: CharterFlightBundleSelection
  occupancies?: Array<App.Occupants>
  cartPrice?: number
  cartMemberPrice?: number
}

export interface CartPanelExperiencePricingInput {
  tripItemId: number
  experienceId: string
  date?: string
  time?: string
  pickupPointId?: string
  redemptionLocationId?: string
  isBuyNowBookLater: boolean
  isGift?: boolean
  bookingType?: string
  ticketModesMap?: Record<string, string>
  tickets: Array<CartPanelExperienceTicket>
  savedPrice?: number
  savedMemberPrice?: number
  savedValue?: number
  cartPrice?: number
  cartMemberPrice?: number
}

export function toTripChatAccommodationPricingInput(
  item: TripChatAccommodationCartCandidate,
  cartItem: App.MultiCartItem | undefined,
): CartPanelAccommodationPricingInput {
  const accommodationCartItem = cartItem && (isMultiCartHotelItem(cartItem) || isMultiCartBedbankHotelItem(cartItem)) ? cartItem : undefined

  return {
    tripItemId: item.tripItemId,
    cartStatus: item.cartStatus,
    offerId: item.offerId,
    packageId: item.packageId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancy: item.rawOccupancy,
    duration: item.duration,
    roomRateId: item.roomRateId,
    isBedbankHotel: item.isBedbankHotel,
    savedPrice: item.savedPrice,
    savedMemberPrice: item.savedMemberPrice,
    savedValue: item.savedValue,
    charterFlightBundle: item.charterFlightBundle,
    occupancies: item.occupancies,
    cartPrice: accommodationCartItem?.item.total,
    cartMemberPrice: accommodationCartItem?.item.memberTotal,
  }
}

function getCartExperienceTotals(cartItem: App.MultiCartExperienceItem | undefined): {
  cartPrice: number | undefined
  cartMemberPrice: number | undefined
} {
  const checkoutItem = getMultiCartExperienceCheckoutItem(cartItem)
  if (!cartItem || !checkoutItem) {
    return {
      cartPrice: undefined,
      cartMemberPrice: undefined,
    }
  }

  const ticketPrice = getCartExperienceTicketTotal(
    cartItem,
    checkoutItem,
    ticket => typeof ticket.price === 'number' ? ticket.price : undefined,
  )
  const ticketMemberPrice = getCartExperienceTicketTotal(
    cartItem,
    checkoutItem,
    ticket => typeof ticket.memberPrice === 'number' ? ticket.memberPrice : undefined,
  )

  return {
    cartPrice: checkoutItem.total ?? ticketPrice,
    cartMemberPrice: checkoutItem.memberTotal ?? ticketMemberPrice,
  }
}

function getCartExperienceTicketTotal(
  cartItem: App.MultiCartExperienceItem,
  checkoutItem: App.Checkout.ExperienceItem,
  getPrice: (ticket: App.ExperienceItemTicket) => number | undefined,
): number | undefined {
  const selectedTickets = checkoutItem.tickets.filter(ticket => ticket.count > 0)
  if (selectedTickets.length === 0) return undefined

  const ticketPrices = selectedTickets.map(ticket => {
    const liveTicket = cartItem.tickets.find(candidate => (
      candidate.productId === ticket.ticketId ||
      candidate.id === ticket.ticketId
    ))
    if (!liveTicket) return undefined

    const price = getPrice(liveTicket)
    return price === undefined ? undefined : price * ticket.count
  })

  return ticketPrices.every((price): price is number => price !== undefined) ? sum(ticketPrices, price => price) : undefined
}

export function toTripChatExperiencePricingInput(
  item: TripChatExperienceCartCandidate,
  cartItem: App.MultiCartItem | undefined,
): CartPanelExperiencePricingInput {
  const experienceCartItem = cartItem?.type === 'experience' ? cartItem : undefined
  const experienceCheckoutItem = getMultiCartExperienceCheckoutItem(experienceCartItem)
  const cartTotals = getCartExperienceTotals(experienceCartItem)
  const tickets = experienceCheckoutItem ? toCartPanelExperienceTickets(experienceCheckoutItem.tickets) : item.tickets

  return {
    tripItemId: item.tripItemId,
    experienceId: item.experienceId,
    date: experienceCartItem?.date ?? item.date,
    time: experienceCartItem ? getCartPanelExperienceCartTime(experienceCartItem) : item.time,
    pickupPointId: experienceCartItem?.pickupPointId ?? item.pickupPointId,
    redemptionLocationId: experienceCartItem?.redemptionLocationId ?? item.redemptionLocationId,
    isBuyNowBookLater: experienceCartItem?.isBuyNowBookLater ?? item.isBuyNowBookLater,
    isGift: experienceCartItem?.isGift,
    bookingType: item.bookingType,
    ticketModesMap: experienceCartItem?.ticketModesMap,
    tickets,
    savedPrice: item.savedPrice,
    savedMemberPrice: item.savedMemberPrice,
    savedValue: item.savedValue,
    cartPrice: cartTotals.cartPrice,
    cartMemberPrice: cartTotals.cartMemberPrice,
  }
}
